/* eslint-disable react/prop-types */
import React from 'react';
import Row from '@amzn/meridian/row';
import Column from '@amzn/meridian/column';
import Button from '@amzn/meridian/button';
import Text from '@amzn/meridian/text';
import NumberField from './NumberField';
import { ZoneCreationProperties } from '../types/zoneMapType';
import { FlowLabel } from '../utils/flowLabel';

/**
 * Props for SelectionToolbar component
 */
export interface SelectionToolbarProps {
  /** Index of the currently active zone */
  activeZoneIdx: number;
  /** Map of all zones being created/edited */
  zones: Map<number, ZoneCreationProperties>;
  /** Callback when active zone changes */
  onActiveZoneChange: (zoneIdx: number) => void;
  /** Whether the toolbar controls should be disabled */
  disabled: boolean;
  /** Whether pick path drawing is currently active */
  isDrawingPickPath?: boolean;
  /** Callback to start drawing a pick path */
  onStartDrawing?: () => void;
  /** Callback to stop/finish drawing a pick path */
  onStopDrawing?: () => void;
  /** Callback to finish the current path */
  onFinishPath?: () => void;
  /** Callback to submit/save the sequence ordering */
  onSubmitSequence?: () => void;
  /** Whether there are pick paths to submit */
  hasPickPaths?: boolean;
  /** Whether sequence submission is in progress */
  isSubmittingSequence?: boolean;
  /** Whether a saved sequence already exists (blocks editing) */
  hasExistingSequence?: boolean;
  /** Callback to undo the last waypoint */
  onUndoWaypoint?: () => void;
  /** Whether undo is available (in-progress path has waypoints) */
  canUndo?: boolean;
  /** Total number of fixtures that need to be sequenced */
  totalFixtureCount?: number;
  /** Number of fixtures currently sequenced (completed + in-progress) */
  sequencedFixtureCount?: number;
  /** GFC pick path custom numbering — start number and step increment. */
  showNumbering?: boolean;
  startNumber?: number;
  stepIncrement?: number;
  onStartNumberChange?: (value: number) => void;
  onStepIncrementChange?: (value: number) => void;
  /**
   * Program-specific noun for the saved sequence (GFC: 'pick path', WFM/Fresh:
   * 'product flow') in its cased forms. See agile/utils/flowLabel.
   */
  flowLabel: FlowLabel;
}

/**
 * SelectionToolbar Component
 *
 * Provides controls for fixture selection operations including:
 * - Active zone indicator and switcher
 * - Pick path drawing controls
 *
 * This toolbar appears above the floorplan viewer when in zone creation/copy mode.
 */
const SelectionToolbar: React.FC<SelectionToolbarProps> = React.memo(
  ({
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    activeZoneIdx: _activeZoneIdx,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    zones: _zones,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    onActiveZoneChange: _onActiveZoneChange,
    disabled,
    isDrawingPickPath = false,
    onStartDrawing,
    onStopDrawing,
    onFinishPath,
    onSubmitSequence,
    hasPickPaths = false,
    isSubmittingSequence = false,
    hasExistingSequence = false,
    onUndoWaypoint,
    canUndo = false,
    totalFixtureCount = 0,
    sequencedFixtureCount = 0,
    showNumbering = false,
    startNumber = 1,
    stepIncrement = 1,
    onStartNumberChange,
    onStepIncrementChange,
    flowLabel,
  }) => {
    const productFlowControls = onStartDrawing ? (
      <Column spacing="200">
        <Text>{flowLabel.title}</Text>
        <Row spacing="200" alignmentVertical="center">
          {hasExistingSequence ? (
            <Text type="b200" color="secondary">
              Sequence saved ✓
            </Text>
          ) : !isDrawingPickPath ? (
            !hasPickPaths ? (
              <>
                <Button
                  type="primary"
                  onClick={onStartDrawing}
                  disabled={disabled}
                  size="small"
                  aria-label={`Start editing ${flowLabel.lower}`}
                >
                  Edit {flowLabel.title}
                </Button>
                <Text type="b100" color="error">
                  No {flowLabel.lower} defined yet. Draw the fixture sequence and save it — it will be reused for future
                  zone map publications.
                </Text>
              </>
            ) : null
          ) : (
            <>
              {showNumbering && (
                <>
                  <NumberField label="Start #" value={startNumber} min={0} onCommit={(n) => onStartNumberChange?.(n)} />
                  <NumberField
                    label="Step"
                    value={stepIncrement}
                    min={1}
                    onCommit={(n) => onStepIncrementChange?.(n)}
                  />
                </>
              )}
              {isDrawingPickPath && canUndo && onUndoWaypoint && (
                <Button type="secondary" onClick={onUndoWaypoint} size="small" aria-label="Undo last waypoint">
                  Undo
                </Button>
              )}
              {onSubmitSequence && (
                <Button
                  type="primary"
                  onClick={() => {
                    if (onFinishPath) onFinishPath();
                    onSubmitSequence();
                  }}
                  disabled={
                    isSubmittingSequence || totalFixtureCount === 0 || sequencedFixtureCount < totalFixtureCount
                  }
                  size="small"
                  aria-label={`Save ${flowLabel.lower} ordering`}
                >
                  {isSubmittingSequence ? 'Saving...' : `Save ${flowLabel.title}`}
                </Button>
              )}
              {onStopDrawing && (
                <Button type="secondary" onClick={onStopDrawing} size="small" aria-label="Stop drawing">
                  Stop Drawing
                </Button>
              )}
            </>
          )}
          {totalFixtureCount > 0 && (
            <Text type="b100" color={sequencedFixtureCount >= totalFixtureCount ? 'primary' : 'secondary'}>
              {sequencedFixtureCount}/{totalFixtureCount} fixtures
            </Text>
          )}
        </Row>
      </Column>
    ) : null;

    // No content → render nothing.
    if (!productFlowControls) {
      return null;
    }

    return (
      <Column backgroundColor="#f5f5f5" width="100%">
        <Row spacing="400" spacingInset="400" alignmentVertical="center" alignmentHorizontal="justify" width="100%">
          {productFlowControls}
        </Row>
      </Column>
    );
  },
);

SelectionToolbar.displayName = 'SelectionToolbar';

export default SelectionToolbar;
